import { useCallback, useEffect, useRef } from 'react';

/**
 * @param T type of save return
 * @param C type of cancel return
 */
export interface IUseBeforeSavePromisesProps<T, C> {
  /** Save action, returned callback waits until all pending edit requests complete before triggering provided save handler  */
  save: (...args: any[]) => Promise<T>;
  /**
   * Cancel action, returned callback that is a NOOP if a save action is already in flight.
   *
   * cancel is intentionally required to remind developers to use this to avoid save/cancel race condition.
   */
  cancel: () => Promise<C> | C;
}

export interface IAddBeforeSavePromises {
  /** A promise that needs to be `await`ed proceeding */
  addBeforeSavePromise?: (id: string, promise: Promise<any>) => void;
  /** Remove a promise that needs to be `await`ed proceeding. */
  removeBeforeSavePromise?: (id: string) => void;
}

export interface IUseBeforeSavePromisesReturn<T, C> extends IAddBeforeSavePromises, IUseBeforeSavePromisesProps<T, C> {}

/**
 * Hook for waiting some other API calls prior to calling some save. Example use case is something that calls service
 * end points and calls ContextProviderService.EditComplete on the server side.
 *
 * If calling editComplete from the client, try `useEditComplete`
 *
 * This is also slightly different from `QueueUtil`, because _order_ does not matter here, just that they all finish.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function useBeforeSavePromises<T, C>({
  save: saveEdits,
  cancel: cancelEdits,
}: IUseBeforeSavePromisesReturn<T, C>) {
  const promises = useRef(new Map<string, Promise<any>>());
  const saving = useRef(false);

  const save = useCallback(
    async (...args: any[]) => {
      // track that save request is in flight:
      saving.current = true;

      // wait for any in-flight handlers (setValue, etc.)
      // _order_ does not matter here, just that they all finish
      await Promise.all(Array.from(promises.current.values()));
      // _then_ Edit Complete:
      const result = await saveEdits(...args);
      saving.current = false;
      return result;
    },
    [saveEdits]
  );

  const cancel = useCallback(() => {
    if (saving.current) {
      // we don't want to cancel edits if the save request is in flight.
      return;
    }
    return cancelEdits();
  }, [cancelEdits]);

  const addBeforeSavePromise = useCallback(
    (id: string, promise: Promise<any>) => promises.current.set(id, promise),
    []
  );
  const removeBeforeSavePromise = useCallback((id: string) => promises.current.delete(id), []);

  useEffect(() => {
    // clear these if the action changes
    promises.current.clear();
  }, [saveEdits]);

  return {
    addBeforeSavePromise,
    removeBeforeSavePromise,
    save,
    cancel,
  };
}
